import React, { useEffect, useState } from 'react';
import { api } from '../../api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ChevronLeft, ChevronRight, Loader2, GraduationCap } from 'lucide-react';

function iso(d) { return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString(); }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate()+n); return x; }
function fmtDay(ds) { const d = new Date(ds); return d.getDate(); }
function dowShort(ds) { return new Date(ds).toLocaleDateString(undefined,{weekday:'short'})[0]; }

const PROG_TONE = {
  '2week_bnt': { bg: '#1B3A87', text: '#fff', label: '2W BnT' },
  '4week_bnt': { bg: '#C8102E', text: '#fff', label: '4W BnT' },
  'private_lesson': { bg: '#B08D57', text: '#fff', label: 'Lesson' },
};

export default function TrainerGrid() {
  const [anchor, setAnchor] = useState(() => { const d = new Date(); d.setHours(0,0,0,0); return d; });
  const [days] = useState(28);
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/training/grid', { params: { start: iso(anchor), days } });
      setGrid(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [anchor.getTime()]);

  return (
    <div className='p-6 lg:p-8'>
      <div className='flex items-center justify-between mb-4'>
        <div>
          <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E] flex items-center gap-1'><GraduationCap className='w-3 h-3' /> Training Schedule</div>
          <h1 className='font-display text-3xl font-bold text-slate-900 mt-1'>Trainer Grid</h1>
          <p className='text-slate-500 text-sm mt-1'>{days} days from {anchor.toLocaleDateString()}. Each row is a trainer — no double bookings.</p>
        </div>
        <div className='flex gap-2'>
          <Button size='sm' variant='outline' className='border-slate-200 text-slate-600 hover:bg-slate-50' onClick={() => setAnchor(addDays(anchor, -7))} data-testid='tgrid-prev'><ChevronLeft className='w-4 h-4' /></Button>
          <Button size='sm' variant='outline' className='border-slate-200 text-slate-600 hover:bg-slate-50' onClick={() => { const d = new Date(); d.setHours(0,0,0,0); setAnchor(d); }} data-testid='tgrid-today'>Today</Button>
          <Button size='sm' variant='outline' className='border-slate-200 text-slate-600 hover:bg-slate-50' onClick={() => setAnchor(addDays(anchor, 7))} data-testid='tgrid-next'><ChevronRight className='w-4 h-4' /></Button>
        </div>
      </div>

      <Card className='p-3 bg-white border-slate-200 overflow-x-auto'>
        {loading && !grid ? (
          <div className='py-20 flex items-center justify-center text-slate-500'><Loader2 className='w-5 h-5 animate-spin mr-2' /> Loading…</div>
        ) : grid && grid.trainers.length === 0 ? (
          <div className='py-12 text-center text-slate-500'>No trainers seeded.</div>
        ) : grid && (
          <table className='min-w-full text-xs' data-testid='trainer-grid-table'>
            <thead>
              <tr>
                <th className='sticky left-0 bg-white z-10 text-left p-2 border-b border-slate-200 font-semibold'>Trainer</th>
                {grid.dates.map(ds => {
                  const d = new Date(ds);
                  const isWeekend = [0,6].includes(d.getDay());
                  return <th key={ds} className={`p-1.5 border-b border-slate-200 font-mono font-semibold tabular-nums ${isWeekend ? 'text-slate-500' : 'text-slate-600'}`}>
                    <div className='text-[9px] uppercase tracking-wider opacity-70'>{dowShort(ds)}</div>
                    <div>{fmtDay(ds)}</div>
                  </th>;
                })}
              </tr>
            </thead>
            <tbody>
              {grid.trainers.map(t => (
                <tr key={t.id}>
                  <td className='sticky left-0 bg-white z-10 p-2 border-b border-slate-200'>
                    <div className='flex items-center gap-2'>
                      {t.avatar_url ? <img src={t.avatar_url} alt={t.full_name} className='w-8 h-8 rounded-full object-cover' /> : <div className='w-8 h-8 rounded-full bg-[#1B3A87] text-slate-900 flex items-center justify-center text-xs font-bold'>{t.full_name[0]}</div>}
                      <div>
                        <div className='font-semibold text-slate-900 text-xs'>{t.full_name}</div>
                        <div className='text-[9px] uppercase tracking-wider text-slate-500'>{t.title || 'Trainer'}</div>
                      </div>
                    </div>
                  </td>
                  {grid.dates.map(ds => {
                    const cell = grid.grid[t.id][ds];
                    const tone = cell ? (PROG_TONE[cell.program_type] || { bg: '#475569', text: '#fff', label: 'Train' }) : null;
                    return (
                      <td key={ds} className='p-0.5 border-b border-slate-200'>
                        {cell ? (
                          <div className='w-full h-10 rounded text-[9px] font-semibold flex flex-col items-center justify-center px-1' style={{background: tone.bg, color: tone.text}} title={`${cell.dog_name} · ${cell.client_name} · ${cell.program_label}`}>
                            <div className='truncate w-full text-center'>{cell.is_start ? cell.dog_name?.substring(0,5) : '—'}</div>
                            {cell.is_start && <div className='text-[8px] opacity-80'>{tone.label}</div>}
                          </div>
                        ) : (
                          <div className='w-full h-10 rounded border border-slate-200 bg-slate-50' />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <div className='flex gap-4 mt-4 text-xs text-slate-500 flex-wrap'>
        {Object.entries(PROG_TONE).map(([k, v]) => (
          <div key={k} className='flex items-center gap-1.5'><div className='w-3 h-3 rounded' style={{background:v.bg}} /> {v.label}</div>
        ))}
        <div className='flex items-center gap-1.5'><div className='w-3 h-3 rounded border border-slate-200' style={{background:'#F8FAFC'}} /> Available</div>
      </div>
    </div>
  );
}
